import { FieldValues, UseFormSetValue, UseFormWatch } from "react-hook-form";

interface UseEditOrderSwapStopsProps {
  setValue: UseFormSetValue<FieldValues>;
  watch: UseFormWatch<FieldValues>;
}

interface UseEditOrderSwapStopsReturn {
  swapStops: () => void;
  canSwap: boolean;
}

/**
 * Custom hook for swapping pickup and dropoff stops in the order edit modal.
 * Swaps address and contact content between the two stops while preserving
 * each stop's identity (id, type, time window, dates).
 * Only allows swapping when there are exactly 2 stops.
 */
function useEditOrderSwapStops({
  setValue,
  watch,
}: UseEditOrderSwapStopsProps): UseEditOrderSwapStopsReturn {
  const watchStops = watch("stops");
  const canSwap = watchStops?.length === 2;

  const swapStops = () => {
    if (!canSwap) return;

    const [firstStop, secondStop] = watchStops;

    const eligibleStopFields = [
      "streetAddress",
      "googlePlaceId",
      "locationId",
      "lngLat",
      "companyName",
      "contactName",
      "phone",
      "suite",
      "instruction",
    ] as const;

    // Visible text inputs must receive "" rather than undefined —
    // setValue(name, undefined) leaves a rendered input showing its stale
    // value. Identifier fields have no input of their own and should stay
    // undefined so submits don't send empty-string ids.
    const visibleTextFields = new Set([
      "streetAddress",
      "companyName",
      "contactName",
      "phone",
      "suite",
      "instruction",
    ]);

    const swapValue = (stop: typeof firstStop, field: string) =>
      visibleTextFields.has(field) ? stop[field] ?? "" : stop[field];

    const swappedFirstStop = { ...firstStop };
    const swappedSecondStop = { ...secondStop };
    eligibleStopFields.forEach((field) => {
      swappedFirstStop[field] = swapValue(secondStop, field);
      swappedSecondStop[field] = swapValue(firstStop, field);
    });

    // The edit modal's stop inputs subscribe with a mix of path notations:
    // AddressInput's display flows from the parent `watch("stops")`, while
    // SuggestionInput / suite / instruction register Controllers with
    // bracket paths like `stops[0].contactName`. react-hook-form matches
    // subscriber names against written names as raw strings, so neither a
    // whole-array write nor bracket per-field writes alone notifies both
    // groups — write both ways (the values are identical, so the double
    // write is idempotent).
    setValue("stops", [swappedFirstStop, swappedSecondStop]);
    eligibleStopFields.forEach((field) => {
      setValue(`stops[0].${field}`, swappedFirstStop[field]);
      setValue(`stops[1].${field}`, swappedSecondStop[field]);
    });
  };

  return {
    swapStops,
    canSwap,
  };
}

export default useEditOrderSwapStops;
